/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreTime — server-safe time and period classes.
 *
 * Exports:
 *   kKeyValueCore   — generic key/value pair
 *   kWorkPeriodCore — duration stored as minutes, with conversion helpers
 *   kTimeCore       — rich date/time value type (no moment.js dependency)
 *
 * No browser APIs. Safe to import in Node.js, Azure Functions, or the browser.
 */

import { getCoreUtils, roundToTwo } from "./kCoreHandler";
import { kCoreTimeParse } from "./kCoreTimeParse";
import { kCoreTimeFormat } from "./kCoreTimeFormat";
import { kCoreTimeNames } from "./kCoreTimeNames";

// ── kKeyValueCore ─────────────────────────────────────────────────────────────

export class kKeyValueCore {
    public key = "";
    public value: any;
}

// ── kWorkPeriodCore ──────────────────────────────────────────────────────────

/** A duration stored as whole minutes, with helpers for display and arithmetic. */
export class kWorkPeriodCore {
    public Minutes = 0;

    /** Whole hours component. */
    public Hours = (): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return Math.floor(this.Minutes / 60);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kWorkPeriodCore.Hours"); return 0; }
    };

    /** Total hours as a decimal rounded to two places (e.g. 1.5 for 90 min). */
    public HoursDec = (): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            return roundToTwo(this.Minutes / 60);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kWorkPeriodCore.HoursDec"); return 0; }
    };

    /** Returns a zero-padded "HH:MM" string (e.g. "01:30" for 90 min). */
    public HoursAndMinutes = (): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const H: number = this.Hours();
            const Remaining: number = this.Minutes - H * 60;
            const Prefix: string = H < 10
                ? ("0" + H.toString()).slice(-2)
                : H.toString();
            return Prefix + ":" + ("0" + Remaining.toString()).slice(-2);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kWorkPeriodCore.HoursAndMinutes"); return ""; }
    };
}

/**
 * `Intl.DateTimeFormatOptions` plus the `dateStyle` / `timeStyle` members, which TypeScript only
 * declares from lib es2020.intl. Declared here (compatible with the newer lib's own declaration) so
 * this file compiles under the ES2017 lib the strict isolated check uses, as well as a newer one -
 * without it every shared file importing kCoreTime inherited two TS2353 errors (PX-17).
 */
export interface kDateStyleOptions extends Intl.DateTimeFormatOptions {
    dateStyle?: "full" | "long" | "medium" | "short";
    timeStyle?: "full" | "long" | "medium" | "short";
}

/** `Intl.RelativeTimeFormat`, declared only from lib es2020.intl - typed here for the ES2017 lib (as kDateStyleOptions). */
export interface IRelativeTimeFormat { format: (Value: number, Unit: string) => string; }
export type IRelativeTimeFormatCtor = new (Locale: string | undefined, Options: { numeric: string }) => IRelativeTimeFormat;
export interface IIntlWithRelativeTime { RelativeTimeFormat?: IRelativeTimeFormatCtor; }

/** Calendar fields in an IANA zone; OffsetMinutes is positive east of UTC. */
export interface kZonedDateParts {
    Year: number; Month: number; Day: number;
    Hour: number; Minute: number; Second: number; OffsetMinutes: number;
}

// ── kTimeCore ─────────────────────────────────────────────────────────────────

/**
 * Rich date/time value type.
 *
 * A NEW INSTANCE IS "NOW", NOT "EMPTY" — `new kTimeCore()` reads the system clock and sets
 * IsValid = true. (This docblock claimed the opposite, "all fields default to 0 / IsValid = false",
 * until 2026-08-05; the constructor below has always said otherwise. The wrong claim is dangerous
 * for an OPTIONAL timestamp field: code that reasons "unset means long ago" gets "unset means this
 * instant" instead, so an age/expiry check silently never expires.) Use `kTimeCore.Unset()` when a
 * field means "no value yet", and test it with `IsValid === false`.
 *
 * Use SetToNow(), fromJSDate(), FromISODate() etc. to populate.
 *
 * Static DateFormat controls ToLocalShortDate output (default "yyyy-MM-dd").
 * Set kTimeCore.DateFormat = "dd/MM/yyyy" to match a regional setting.
 */
export class kTimeCore {

    /**
     * Descending lexical order of ISO-keyed records; no date parsing or normalization.
     * DOCUMENTED EXCEPTION: pure sort comparator; caller owns its guard and diagnostics.
     * ERROR-PROPAGATION-JUSTIFIED: preserve native getter/coercion faults and comparison order.
     */
    public static NewestIsoFirst = (A: { LastIso: string }, B: { LastIso: string }): number => {
        if (A.LastIso > B.LastIso) { return -1; }
        if (A.LastIso < B.LastIso) { return 1; }
        return 0;
    };

    /**
     * Unix seconds -> ISO-8601 UTC text ("2026-10-19T02:30:00.000Z"); "" for 0, a negative or a non-finite value
     * (the "not set" of a Key Vault / WhatsApp timestamp). Added 2026-10-19 for three local copies (reuse #1597).
     */
    public static IsoOfSeconds = (Sec: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (typeof Sec !== "number" || isFinite(Sec) === false || Sec <= 0) { return ""; }
            return new Date(Sec * 1000).toISOString();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.IsoOfSeconds");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: pure field projection without validity, year cutoff, gate or catch.
     * Caller owns validity/reporting. Reads and String-coerces Month, Day, Year via separate calls.
     */
    public static FormatYmd = (ReadFields: () => { Year: number; Month: number; Day: number }): string => {
        const M: string = ("0" + String(ReadFields().Month)).slice(-2);
        const D: string = ("0" + String(ReadFields().Day)).slice(-2);
        return String(ReadFields().Year) + "-" + M + "-" + D;
    };

    /** DOCUMENTED EXCEPTION: pure native local-date projection; caller owns reporting.
     * Preserve own-toString coercion order and an optional cached day, even with a latched gate.
     * Unlike ToISODate this deliberately formats historical and invalid native date fields.
     */
    public static FormatDateKey = (Value: Date, CachedDay?: number): string => {
        const Year: string = Value.getFullYear().toString();
        const Month: string = (Value.getMonth() + 1).toString().padStart(2, "0");
        let Day: number | undefined = CachedDay;
        if (Day === undefined) { Day = Value.getDate(); }
        return Year + "-" + Month + "-" + Day.toString().padStart(2, "0");
    };

    /** Reads an instant without changing it. Invalid dates/zones report centrally and return null. */
    public static GetZonedDateParts = (Instant: Date, TimeZone: string): kZonedDateParts | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Options: Intl.DateTimeFormatOptions & { hourCycle: "h23" } = {
                timeZone: TimeZone, year: "numeric", month: "2-digit", day: "2-digit",
                hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
            };
            const Fields: Record<string, number> = {};
            for (const Part of new Intl.DateTimeFormat("en-GB", Options).formatToParts(Instant)) {
                if (Part.type !== "literal") { Fields[Part.type] = Number(Part.value); }
            }
            const WallUtc: number = Date.UTC(Fields.year, Fields.month - 1, Fields.day,
                Fields.hour, Fields.minute, Fields.second);
            return { Year: Fields.year, Month: Fields.month, Day: Fields.day,
                Hour: Fields.hour, Minute: Fields.minute, Second: Fields.second,
                OffsetMinutes: (WallUtc - Math.floor(Instant.getTime() / 1000) * 1000) / 60000 };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.GetZonedDateParts");
            return null;
        }
    };

    public Year: number;
    public Month: number;
    public Day: number;
    public Hour: number;
    public Minute: number;
    public Second: number;
    public MilliSecond: number;
    public IsValid: boolean;

    constructor() {
        const Now: Date = new Date();
        this.Year = Now.getFullYear();
        this.Month = Now.getMonth() + 1;
        this.Day = Now.getDate();
        this.Hour = Now.getHours();
        this.Minute = Now.getMinutes();
        this.Second = Now.getSeconds();
        this.MilliSecond = Now.getMilliseconds();
        this.IsValid = true;
    }

    /** Regional date format used by ToLocalShortDate. Override once at app startup. */
    public static DateFormat = "yyyy-MM-dd";

    /**
     * A timestamp meaning "no value yet" — the safe initialiser for an OPTIONAL date field.
     *
     * `new kTimeCore()` is NOW, so `public SomeDate: kTimeCore = new kTimeCore()` makes an unset
     * field look like it was set this instant. Any "has enough time passed?" test written against
     * such a field then answers "no" for ever and never expires. This returns the zeroed,
     * IsValid = false state that a failed FromISODate/FromISODateTime parse also produces, so
     * "never set" and "could not be read" are one check: `IsValid === false`.
     */
    public static Unset = (): kTimeCore => {
        const Empty: kTimeCore = new kTimeCore();
        try {
            Empty.Year = 0; Empty.Month = 0; Empty.Day = 0;
            Empty.Hour = 0; Empty.Minute = 0; Empty.Second = 0; Empty.MilliSecond = 0;
            Empty.IsValid = false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.Unset");
            Empty.IsValid = false;
        }
        return Empty;
    };

    // ── Comparison ────────────────────────────────────────────────────────────

    /** True if this date/time is in the past. */
    public isBefore = (): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return this.ToJsDate() < new Date();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.isBefore"); return false; }
    };

    /** True if the weekday is Saturday (6) or Sunday (0). */
    public isWeekend = (): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Day: number = this.Weekday();
            return Day === 0 || Day === 6;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.isWeekend"); return false; }
    };

    /** True if this date matches today's date (ignores time). */
    public IsToday = (): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            const Today: Date = new Date();
            return this.Year === Today.getFullYear()
                && this.Month === Today.getMonth() + 1
                && this.Day === Today.getDate();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.IsToday"); return false; }
    };

    /** Number of calendar days from this date to OtherDate (inclusive). */
    public DaysToOtherDate = (OtherDate: kTimeCore): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            const MsPerDay = 86400000;
            return Math.floor(
                (OtherDate.TreatAsUTC().getTime() - this.TreatAsUTC().getTime()) / MsPerDay
            ) + 1;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.DaysToOtherDate"); return 0; }
    };

    // ── Mutation ──────────────────────────────────────────────────────────────

    /** Set time fields to midnight (00:00:00.000). */
    public SetTimeToBeginningOfDay = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Hour = 0; this.Minute = 0; this.Second = 0; this.MilliSecond = 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetTimeToBeginningOfDay"); }
    };

    /** Set time fields to end of day (23:59:59.099). */
    public SetTimeToEndOfDay = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Hour = 23; this.Minute = 59; this.Second = 59; this.MilliSecond = 99;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetTimeToEndOfDay"); }
    };

    /** Parse "HH:MM" or "H:MM" and set Hour and Minute accordingly. */
    public SetTimeFromString = (TimeString: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Parts: string[] = TimeString.split(":");
            this.Hour = parseInt(Parts[0]);
            this.Minute = parseInt(Parts[1]);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetTimeFromString"); }
    };

    /** Move to Monday of the current week at midnight. */
    public SetTimeToBeginningOfWeek = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.SetTimeToBeginningOfDay();
            if (this.Weekday() === 0) {
                this.Add("day", -6);
            } else {
                this.Add("day", -this.Weekday() + 1);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetTimeToBeginningOfWeek"); }
    };

    /** Move to Sunday of the current week at end-of-day. */
    public SetTimeToEndOfWeek = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.SetTimeToBeginningOfWeek();
            this.SetTimeToEndOfDay();
            this.Add("day", 6);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetTimeToEndOfWeek"); }
    };

    /** Set day to 1 and time to midnight. */
    public SetDayToFirstOfMonth = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.Day = 1;
            this.SetTimeToBeginningOfDay();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetDayToFirstOfMonth"); }
    };

    /** Advance to the last day of the current month. */
    public SetDayToLastOfMonth = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const StartMonth: number = this.Month;
            this.Day = 0;
            for (let Counter = 0; Counter <= 31; Counter++) {
                this.AddDays(1);
                if (this.Month !== StartMonth) { break; }
            }
            this.AddDays(-1);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetDayToLastOfMonth"); }
    };

    /** Set all fields from the current system time. */
    public SetToNow = (): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            this.fromJSDate(new Date());
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SetToNow"); }
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.Add
    public Add = (interval: string, units: number): void => { return kCoreTimeParse.Add(this, interval, units); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.AddDays
    public AddDays = (DaysToAdd: number): Date | null => { return kCoreTimeParse.AddDays(this, DaysToAdd); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.AddBusinessDays
    public AddBusinessDays = (Days: number, NonWorkingDates: string[] = []): Date | null => {
        return kCoreTimeParse.AddBusinessDays(this, Days, NonWorkingDates);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.IsWorkingDay
    public IsWorkingDay = (NonWorkingDates: string[] = []): boolean => { return kCoreTimeParse.IsWorkingDay(this, NonWorkingDates); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.AddMinutes
    public AddMinutes = (MinutesToAdd: number): Date | null => { return kCoreTimeParse.AddMinutes(this, MinutesToAdd); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.SubractMinutes
    public SubractMinutes = (MinutesToSubtract: number): Date | null => { return kCoreTimeParse.SubractMinutes(this, MinutesToSubtract); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.SubractDays
    public SubractDays = (DaysToSubtract: number): Date | null => { return kCoreTimeParse.SubractDays(this, DaysToSubtract); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.ToJsDate
    public ToJsDate = (): Date => { return kCoreTimeParse.ToJsDate(this); };

    /** Returns ToJsDate() shifted to UTC (timezone offset removed). */
    public TreatAsUTC = (): Date => {
        const Result: Date = this.ToJsDate();
        Result.setMinutes(Result.getMinutes() - Result.getTimezoneOffset());
        return Result;
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromJSDate
    public fromJSDate = (NewDate: Date): void => { return kCoreTimeParse.FromJSDate(this, NewDate); };

    /** Alias for FromISODate — accepts "yyyy-MM-dd" strings. */
    public fromISO = (IsoStringDate: string): void => {
        this.FromISODate(IsoStringDate);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromISODate
    public FromISODate = (IsoStringDate: string): void => { return kCoreTimeParse.FromISODate(this, IsoStringDate); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromISODateTime
    public FromISODateTime = (IsoStringDate: string): void => { return kCoreTimeParse.FromISODateTime(this, IsoStringDate); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromCompactDateTime
    public FromCompactDateTime = (YearToSec: string): void => { return kCoreTimeParse.FromCompactDateTime(this, YearToSec); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromJSON
    public FromJSON = (JSONObj: any): void => { return kCoreTimeParse.FromJSON(this, JSONObj); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromSPDate
    public FromSPDate = (SPDate: string, AllDay?: boolean, _Bias?: number): void => {
        return kCoreTimeParse.FromSPDate(this, SPDate, AllDay, _Bias);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeParse.FromSPFXDate
    public FromSPFXDate = (SPFXDate: string): void => { return kCoreTimeParse.FromSPFXDate(this, SPFXDate); };

    // ── ISO formatting ────────────────────────────────────────────────────────

    /** DOCUMENTED EXCEPTION: assignments only; resets a failed parser to the optional-date state. */
    private ClearDate = (): void => {
        this.Year = 0; this.Month = 0; this.Day = 0;
        this.Hour = 0; this.Minute = 0; this.Second = 0; this.MilliSecond = 0;
        this.IsValid = false;
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISODate
    public ToISODate = (): string => { return kCoreTimeFormat.ToISODate(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISOTime
    public ToISOTime = (): string => { return kCoreTimeFormat.ToISOTime(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISODateTime
    public ToISODateTime = (): string => { return kCoreTimeFormat.ToISODateTime(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISOZuluDate
    public ToISOZuluDate = (): string => { return kCoreTimeFormat.ToISOZuluDate(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISOZuluDateTime
    public ToISOZuluDateTime = (): string => { return kCoreTimeFormat.ToISOZuluDateTime(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToISODateTimeSecs
    public ToISODateTimeSecs = (): string => { return kCoreTimeFormat.ToISODateTimeSecs(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat._FormatShortDate
    protected _FormatShortDate = (Fmt: string): string => { return kCoreTimeFormat._FormatShortDate(this, Fmt); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToLocalShortDate
    public ToLocalShortDate = (): string => { return kCoreTimeFormat.ToLocalShortDate(this, kTimeCore.DateFormat); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToLocalShortDateAndTime
    public ToLocalShortDateAndTime = (): string => { return kCoreTimeFormat.ToLocalShortDateAndTime(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.ToSmartLocalDate
    public ToSmartLocalDate = (Locale: string = "", RecentDays: boolean = false): string => {
        return kCoreTimeFormat.ToSmartLocalDate(this, Locale, RecentDays);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.SmartLocalText
    public static SmartLocalText = (When: Date, Locale: string = "", RecentDays: boolean = false, Now: Date = new Date()): string => {
        return kCoreTimeFormat.SmartLocalText(this, When, Locale, RecentDays, Now);
    };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeFormat.To2DigitTime
    public To2DigitTime = (): string => { return kCoreTimeFormat.To2DigitTime(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.WeekdayName
    public WeekdayName = (Locale: string = ""): string => { return kCoreTimeNames.WeekdayName(this, Locale); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.ToLongDate
    public ToLongDate = (Locale: string = ""): string => { return kCoreTimeNames.ToLongDate(this, Locale); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.ToLongDateTime
    public ToLongDateTime = (Locale: string = ""): string => { return kCoreTimeNames.ToLongDateTime(this, Locale); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames._UseLocale
    private _UseLocale = (Locale: string): string => { return kCoreTimeNames._UseLocale(this, Locale); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.WeekDayName2
    public WeekDayName2 = (): string => { return kCoreTimeNames.WeekDayName2(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.MonthName
    public MonthName = (Locale: string = ""): string => { return kCoreTimeNames.MonthName(this, Locale); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.Weekday
    public Weekday = (): number => { return kCoreTimeNames.Weekday(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.WeekNumber
    public WeekNumber = (): number => { return kCoreTimeNames.WeekNumber(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.YearWeekNumber
    public YearWeekNumber = (): number => { return kCoreTimeNames.YearWeekNumber(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames._IsoThursday
    private _IsoThursday = (): Date => { return kCoreTimeNames._IsoThursday(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.MSecsFromNow
    public MSecsFromNow = (): number => { return kCoreTimeNames.MSecsFromNow(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.SecondsFromNow
    public SecondsFromNow = (): number => { return kCoreTimeNames.SecondsFromNow(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.SecsFromNow
    public SecsFromNow = (): number => { return kCoreTimeNames.SecsFromNow(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.MinutesFromNow
    public MinutesFromNow = (): number => { return kCoreTimeNames.MinutesFromNow(this); };

    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kCoreTimeNames.Clone
    public Clone = (): kTimeCore => { return kCoreTimeNames.Clone(this, () => new kTimeCore()); };
}
